import { useState } from 'react'
import { useParams } from 'react-router'
import { Info } from 'lucide-react'
import {
  DateValue, EmptyState, FileUploader, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import type { UploadedFile } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useLibraryDocument, useLibraryMutations, useLibraryVersions } from '../api'
import { LIBRARY_KIND_LABELS, LIBRARY_STATUS_LABELS } from '../types'
import type { LibraryKind, LibraryStatus } from '../types'

const STATUS_TONE: Record<LibraryStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
}

/** One library entry: its versions, and what asking people about them would mean. */
export function LibraryDocumentPage() {
  useBreadcrumb()
  const { id } = useParams<{ id: string }>()
  const document = useLibraryDocument(id)
  const versions = useLibraryVersions(id)
  const { publish, archive, openCampaign } = useLibraryMutations()

  const [publishDraft, setPublishDraft] = useState<{
    storedFileId: string; fileName: string; changeSummary: string; effectiveFrom: string
  } | null>(null)
  const [campaignDraft, setCampaignDraft] = useState<{
    versionId: string; name: string; dueOn: string
  } | null>(null)
  const [archiveDraft, setArchiveDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  if (document.isPending) {
    return <Skeleton className="h-64" />
  }
  if (!document.isSuccess) {
    return <EmptyState title="That entry could not be loaded" description="It may have been removed." />
  }

  const current = versions.data?.find((version) => version.current)

  return (
    <div className="space-y-4">
      <PageHeader
        title={document.data.title}
        subtitle={[
          LIBRARY_KIND_LABELS[document.data.kind as LibraryKind] ?? document.data.kind,
          document.data.folderName,
          document.data.ownerName,
        ].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setPublishDraft({
                  storedFileId: '', fileName: '', changeSummary: '', effectiveFrom: '',
                })
              }}
            >
              Publish a version
            </Button>
            {document.data.kind === 'POLICY' && current && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setError(null)
                  setCampaignDraft({
                    versionId: current.id,
                    name: `${document.data.title} v${current.versionNo}`,
                    dueOn: '',
                  })
                }}
              >
                Ask people to acknowledge
              </Button>
            )}
            {document.data.status !== 'ARCHIVED' && (
              <Button type="button" variant="outline" onClick={() => setArchiveDraft('')}>
                Withdraw
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="space-y-2 p-4 text-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[var(--color-heading)]">Where it stands</h2>
            <StatusBadge
              status={document.data.status}
              tone={STATUS_TONE[document.data.status as LibraryStatus] ?? 'neutral'}
              label={LIBRARY_STATUS_LABELS[document.data.status as LibraryStatus]
                ?? document.data.status}
            />
          </div>
          <p className="text-slate-600">
            {document.data.currentVersionNo == null
              ? 'Nothing has been published, so there is nothing anybody can be asked to read.'
              : `Version ${document.data.currentVersionNo} is current, of ${document.data.versionCount} published.`}
          </p>
          {document.data.summary && <p className="text-slate-600">{document.data.summary}</p>}
          {document.data.tags.length > 0 && (
            <p className="text-slate-600">Tags: {document.data.tags.join(', ')}</p>
          )}
          {document.data.archiveReason && (
            <p className="text-amber-800">Withdrawn: {document.data.archiveReason}</p>
          )}
        </Card>

        <Card className="space-y-2 p-4 text-sm">
          <h2 className="font-semibold text-[var(--color-heading)]">Review and retention</h2>
          <p className="flex items-start gap-2 text-slate-600">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {document.data.alertNote}
          </p>
          <p className="text-slate-600">
            {document.data.retentionRuleName
              ? `Retention: ${document.data.retentionRuleName}. Nothing is ever deleted automatically.`
              : 'No retention rule is attached, so nothing here says when this may be destroyed. That is a gap, not a permission.'}
          </p>
        </Card>
      </div>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Versions</h2>
        {versions.isSuccess && versions.data.length === 0 && (
          <EmptyState
            title="Nothing has been published"
            description="A version is a file plus the date it took effect. Versions are never edited afterwards, because an acknowledgement names the version somebody was shown."
          />
        )}
        {versions.isSuccess && versions.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Version</th>
                  <th className="p-3 text-start font-medium">Published</th>
                  <th className="p-3 text-start font-medium">Effective</th>
                  <th className="p-3 text-start font-medium">What changed</th>
                  <th className="p-3 text-start font-medium">State</th>
                </tr>
              </thead>
              <tbody>
                {versions.data.map((version) => (
                  <tr key={version.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">v{version.versionNo}</td>
                    <td className="p-3">
                      {version.publishedAt
                        ? <DateValue value={version.publishedAt} /> : 'Not published'}
                    </td>
                    <td className="p-3">
                      {version.effectiveFrom
                        ? <DateValue value={version.effectiveFrom} /> : '—'}
                    </td>
                    <td className="p-3 max-w-md text-slate-600">{version.changeSummary ?? '—'}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={version.current ? 'CURRENT' : 'SUPERSEDED'}
                        tone={version.current ? 'success' : 'neutral'}
                        label={version.current ? 'Current' : 'Superseded'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={publishDraft != null}
        onOpenChange={(open) => { if (!open) { setPublishDraft(null); setDialogError(null) } }}
        title="Publish a version"
        description="The current version is superseded, not replaced: what people acknowledged stays exactly as they saw it."
        submitLabel="Publish"
        loading={publish.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!publishDraft || !id) return
          act(async () => {
            await publish.mutateAsync({
              id,
              body: {
                storedFileId: publishDraft.storedFileId,
                changeSummary: publishDraft.changeSummary || null,
                effectiveFrom: publishDraft.effectiveFrom || null,
              },
            })
            setPublishDraft(null)
          }, 'That version could not be published', true)
        }}
      >
        {publishDraft && (
          <div className="space-y-3">
            <div>
              <Label>The file</Label>
              <FileUploader
                ownerType="library_document"
                ownerId={id}
                files={publishDraft.storedFileId
                  ? [{ id: publishDraft.storedFileId, filename: publishDraft.fileName,
                       sizeBytes: 0, contentType: null } as UploadedFile]
                  : []}
                onUploaded={(file) => setPublishDraft({
                  ...publishDraft, storedFileId: file.id, fileName: file.filename })}
                onRemoved={() => setPublishDraft({
                  ...publishDraft, storedFileId: '', fileName: '' })}
              />
            </div>
            <div>
              <Label htmlFor="version-summary">What changed</Label>
              <Input
                id="version-summary" value={publishDraft.changeSummary}
                onChange={(event) =>
                  setPublishDraft({ ...publishDraft, changeSummary: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="version-effective">Effective from</Label>
              <DatePicker
                id="version-effective" value={publishDraft.effectiveFrom}
                onChange={(event) =>
                  setPublishDraft({ ...publishDraft, effectiveFrom: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={campaignDraft != null}
        onOpenChange={(open) => { if (!open) { setCampaignDraft(null); setDialogError(null) } }}
        title="Ask people to acknowledge this version"
        description="Everybody in scope right now is written down as being asked. Somebody who joins next week is not retrospectively outstanding."
        submitLabel="Open the campaign"
        loading={openCampaign.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!campaignDraft) return
          act(async () => {
            await openCampaign.mutateAsync({
              versionId: campaignDraft.versionId,
              name: campaignDraft.name,
              audienceKind: 'ALL',
              dueOn: campaignDraft.dueOn || null,
            })
            setCampaignDraft(null)
          }, 'That campaign could not be opened', true)
        }}
      >
        {campaignDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="campaign-name">Name</Label>
              <Input
                id="campaign-name" value={campaignDraft.name}
                onChange={(event) =>
                  setCampaignDraft({ ...campaignDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="campaign-due">Answer by</Label>
              <DatePicker
                id="campaign-due" value={campaignDraft.dueOn}
                onChange={(event) =>
                  setCampaignDraft({ ...campaignDraft, dueOn: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={archiveDraft != null}
        onOpenChange={(open) => { if (!open) { setArchiveDraft(null); setDialogError(null) } }}
        title="Withdraw this entry"
        description="Say why. A policy that vanished without a reason is the one somebody asks about later."
        submitLabel="Withdraw"
        loading={archive.isPending}
        error={dialogError}
        onSubmit={() => {
          if (archiveDraft == null || !id) return
          act(async () => {
            await archive.mutateAsync({ id, reason: archiveDraft })
            setArchiveDraft(null)
          }, 'That entry could not be withdrawn', true)
        }}
      >
        {archiveDraft != null && (
          <div>
            <Label htmlFor="archive-reason">Why</Label>
            <Input
              id="archive-reason" value={archiveDraft}
              onChange={(event) => setArchiveDraft(event.target.value)}
            />
          </div>
        )}
      </FormDialog>
    </div>
  )
}
